import type { ReactNode } from "react";
import type { Seat } from "@guandan/engine";
import {
  Avatar,
  type AvatarConnection,
  type AvatarExpression,
  type AvatarReaction,
} from "../art/Avatar.js";
import type { TableLayout } from "./layout.js";

/**
 * The four players, placed around a table.
 *
 * Shared by the live table, the waiting room and the replay viewer so all three
 * seat people identically — the waiting room is the screen a player stares at
 * immediately before the deal, and a seating arrangement that shifts at that
 * moment reads as the game restarting rather than beginning.
 *
 * Purely presentational: it takes a computed `TableLayout` and what to draw in
 * each seat, and knows nothing about turns, intents or sockets.
 */
export interface SeatRingEntry {
  readonly name: string;
  readonly avatarId: string;
  readonly expression?: AvatarExpression;
  readonly reaction?: AvatarReaction;
  readonly connection?: AvatarConnection;
  /** Highlights the seat and rings the avatar. */
  readonly onTurn?: boolean;
  /** The count chip under the name — cards in play, ready state while waiting. */
  readonly badge?: ReactNode;
  /** A transient chip over the seat: "pass" at the table, "host" in a lobby. */
  readonly note?: ReactNode;
  /** Drawn inside the avatar well, over the portrait. Used for the turn clock. */
  readonly overlay?: ReactNode;
  /** An unclaimed seat, drawn back so a real player reads as present. */
  readonly empty?: boolean;
}

export function SeatRing({ table, entryFor }: {
  table: TableLayout;
  entryFor: (seat: Seat) => SeatRingEntry | null;
}) {
  return (
    <>
      {table.seats.map((box) => {
        const entry = entryFor(box.seat);
        if (entry === null) return null;
        return (
          <div
            key={box.seat}
            className={`seat seat-rel-${box.relative}${entry.empty === true ? " seat-empty" : ""}`}
            style={{ left: box.x, top: box.y }}
          >
            <div className="seat-avatar">
              <Avatar
                avatarId={entry.avatarId}
                expression={entry.expression}
                size={box.avatarSize}
                team={box.seat % 2 as 0 | 1}
                turn={entry.onTurn === true && entry.overlay === undefined}
                connection={entry.connection}
                reaction={entry.reaction ?? reactionFor(entry.expression)}
              />
              {entry.overlay}
            </div>
            <span className="seat-label">{entry.name}</span>
            {entry.badge !== undefined && (
              <span className={`seat-count tabular team-${box.seat % 2}`}>{entry.badge}</span>
            )}
            {entry.note}
          </div>
        );
      })}
    </>
  );
}

function reactionFor(expression: AvatarExpression | undefined): AvatarReaction | undefined {
  if (expression === undefined || expression === "idle" || expression === "thinking") return undefined;
  return expression;
}
